import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, Calendar, Filter, X, ArrowLeft, Loader2, AlertCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { reportsAPI, getErrorMessage } from '../../lib/api';
import SecurePDFViewer from '../../components/SecurePDFViewer';
import { UpgradeOverlay } from '../../components/UpgradeOverlay';

const PerformanceReportsContent = () => {
  const { id: reportIdFromUrl } = useParams();
  const navigate = useNavigate();
  
  // State for list view
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    closed: 0,
  });

  // State for detail view
  const [selectedReport, setSelectedReport] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // State for filters - pending (form inputs) and applied (actual filtering)
  const [pendingFilters, setPendingFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
  });

  // Fetch reports from API
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await reportsAPI.getReports({
        page: pagination.page,
        page_size: pagination.pageSize,
      });

      const { data, pagination: paginationData, stats: apiStats } = response.data;
      
      setReports(data || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData?.total || 0,
        totalPages: paginationData?.totalPages || 1,
      }));
      
      // Update stats from API response if available
      if (apiStats) {
        setStats({
          total: apiStats.total || 0,
          open: apiStats.open || 0,
          closed: apiStats.closed || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize]);

  // Initial fetch
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Load specific report if ID is in URL
  useEffect(() => {
    if (reportIdFromUrl && reports.length > 0) {
      const report = reports.find(r => r.id.toString() === reportIdFromUrl);
      if (report) {
        handleViewReport(report);
      }
    }
  }, [reportIdFromUrl, reports]);

  // Fetch PDF for viewing
  const fetchPDF = useCallback(async (reportId) => {
    setPdfLoading(true);
    setPdfError(null);
    setPdfBlob(null);

    try {
      const response = await reportsAPI.getReportPDF(reportId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfBlob(blob);
    } catch (err) {
      console.error('Failed to fetch PDF:', err);
      setPdfError(getErrorMessage(err));
    } finally {
      setPdfLoading(false);
    }
  }, []);

  // Handle pending filter change (form inputs)
  const handleFilterChange = (field, value) => {
    setPendingFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    setAppliedFilters({ ...pendingFilters });
  };

  // Clear filters
  const handleClearFilters = () => {
    const emptyFilters = {
      dateFrom: '',
      dateTo: '',
      status: '',
    };
    setPendingFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  // View report detail
  const handleViewReport = async (report) => {
    setSelectedReport(report);
    await fetchPDF(report.id);
  };

  // Back to list
  const handleBackToList = () => {
    setSelectedReport(null);
    setPdfBlob(null);
    setPdfError(null);
    // Navigate back to reports list if we came from a direct URL
    if (reportIdFromUrl) {
      navigate('/dashboard/reports');
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      handlePageChange(pagination.page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      handlePageChange(pagination.page + 1);
    }
  };

  // Format date from API (YYYY-MM-DD) to display format (DD-MM-YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(/\//g, '-');
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return '-';
    return `₹${parseFloat(price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '-';
    const num = parseFloat(value);
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  // Filter reports locally using applied filters
  const filteredReports = reports.filter((report) => {
    // Status filter
    if (appliedFilters.status && report.status?.toLowerCase() !== appliedFilters.status.toLowerCase()) {
      return false;
    }

    // Date from filter
    if (appliedFilters.dateFrom) {
      const reportDate = new Date(report.recommendation_date);
      const filterDate = new Date(appliedFilters.dateFrom);
      if (reportDate < filterDate) return false;
    }

    // Date to filter
    if (appliedFilters.dateTo) {
      const reportDate = new Date(report.recommendation_date);
      const filterDate = new Date(appliedFilters.dateTo);
      if (reportDate > filterDate) return false;
    }

    return true;
  });

  // Stats are now managed by state and populated from API response
  // No need to recalculate from current page's reports

  // Detail View
  if (selectedReport) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={handleBackToList}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Performance Reports
        </button>

        {/* Report Details */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {selectedReport.recommendation_stock}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Recommendation Date</p>
              <p className="font-semibold text-gray-900">
                {formatDate(selectedReport.recommendation_date)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Buy Price</p>
              <p className="font-semibold text-gray-900">
                {formatPrice(selectedReport.recommended_price)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Exit Price</p>
              <p className="font-semibold text-gray-900">
                {formatPrice(selectedReport.exit_price)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Gain/Loss</p>
              <p className={`font-semibold ${
                parseFloat(selectedReport.percentage_gain_loss) >= 0 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                {formatPercentage(selectedReport.percentage_gain_loss)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Status</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                selectedReport.status?.toLowerCase() === 'open'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {selectedReport.status || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* PDF Viewer */}
        {pdfLoading ? (
          <div className="bg-white rounded-lg shadow-lg p-12 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
              <p className="text-gray-600">Loading report PDF...</p>
            </div>
          </div>
        ) : pdfError ? (
          <div className="bg-white rounded-lg shadow-lg p-12 flex items-center justify-center">
            <div className="text-center text-red-500">
              <AlertCircle className="mx-auto mb-3" size={48} />
              <p className="font-semibold mb-2">Failed to load PDF</p>
              <p className="text-sm text-gray-500">{pdfError}</p>
              <button
                onClick={() => fetchPDF(selectedReport.id)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
              >
                <RefreshCw size={18} />
                Retry
              </button>
            </div>
          </div>
        ) : pdfBlob ? (
          <SecurePDFViewer pdfBlob={pdfBlob} height="700px" />
        ) : null}
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
          <p className="text-gray-600">Loading performance reports...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-3 text-red-500" size={48} />
          <p className="text-red-600 font-semibold mb-2">Failed to load reports</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={fetchReports}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Performance Reports</h2>
        <p className="text-gray-600">
          View detailed performance reports for all stock recommendations
        </p>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="text-blue-600" size={20} />
          <h3 className="text-lg font-bold text-gray-900">Filter Reports</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Date From
            </label>
            <input
              type="date"
              value={pendingFilters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Date To
            </label>
            <input
              type="date"
              value={pendingFilters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Status
            </label>
            <select
              value={pendingFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="md:col-span-3 flex items-end gap-2">
            <button
              onClick={handleApplyFilters}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <Filter size={18} />
              Filter
            </button>
            <button
              onClick={handleClearFilters}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <X size={18} />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">
                  STOCK NAME
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">
                  RECOMMENDATION DATE
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">
                  BUY PRICE
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">
                  EXIT PRICE
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">
                  % GAIN/LOSS
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">
                  STATUS
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">
                  VIEW
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredReports.map((report, index) => (
                <tr key={report.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-semibold text-gray-900">
                      {report.recommendation_stock}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(report.recommendation_date)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-900 font-semibold">
                    {formatPrice(report.recommended_price)}
                  </td>
                  <td className="px-6 py-4 text-gray-900 font-semibold">
                    {formatPrice(report.exit_price)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-semibold ${
                      parseFloat(report.percentage_gain_loss) >= 0 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {formatPercentage(report.percentage_gain_loss)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      report.status?.toLowerCase() === 'open'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {report.status || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleViewReport(report)}
                      className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors font-semibold text-sm flex items-center gap-2"
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredReports.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No performance reports found</p>
            <p className="text-gray-400 text-sm">
              {reports.length > 0 ? 'Try adjusting your filters' : 'No reports available yet'}
            </p>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} reports
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={pagination.page === 1}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pagination.page === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      pageNum === pagination.page
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={handleNextPage}
                disabled={pagination.page === pagination.totalPages}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pagination.page === pagination.totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Total Reports</p>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Open Positions</p>
          <p className="text-3xl font-bold text-green-600">{stats.open}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Closed Positions</p>
          <p className="text-3xl font-bold text-gray-600">{stats.closed}</p>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with access control
const PerformanceReports = () => {
  return (
    <UpgradeOverlay service="performance_report">
      <PerformanceReportsContent />
    </UpgradeOverlay>
  );
};

export default PerformanceReports;
